/**
 * principal-context.tsx
 *
 * React context for current authenticated Principal Identity.
 * Located in src/shell/identity/ for clean architectural boundary separation.
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';

export interface PrincipalIdentity {
  principalId: string;
  nodeId: string;
  displayName: string;
  hxnAlias?: string;
  hnsAlias?: string;
  avatarIcon: string;
  isOwner?: boolean;
}

export interface PrincipalContextValue extends PrincipalIdentity {
  /**
   * Authoritative helper to determine if an entity/author represents the local current user.
   */
  isSelf: (authorPrincipalId?: string, authorDisplayName?: string, authorId?: string) => boolean;
}

const PrincipalContext = createContext<PrincipalContextValue | null>(null);

export interface PrincipalProviderProps {
  identity: PrincipalIdentity;
  children: ReactNode;
}

export const PrincipalProvider: React.FC<PrincipalProviderProps> = ({ identity, children }) => {
  const value = useMemo<PrincipalContextValue>(() => {
    return {
      ...identity,
      isSelf: (authorPrincipalId?: string, authorDisplayName?: string, authorId?: string): boolean => {
        if (authorPrincipalId && authorPrincipalId === identity.principalId) {
          return true;
        }
        if (authorId && (authorId === identity.nodeId || (authorId === 'user' && identity.isOwner))) {
          return true;
        }
        if (authorPrincipalId && authorPrincipalId === identity.nodeId) {
          return true;
        }
        if (
          authorDisplayName &&
          identity.displayName &&
          authorDisplayName.toLowerCase().trim() === identity.displayName.toLowerCase().trim() &&
          authorDisplayName !== 'You' &&
          authorDisplayName !== 'User'
        ) {
          return true;
        }
        if (authorPrincipalId === 'self' || authorId === 'self') {
          return true;
        }
        return false;
      },
    };
  }, [identity]);

  return <PrincipalContext.Provider value={value}>{children}</PrincipalContext.Provider>;
};

export function useCurrentPrincipal(): PrincipalContextValue {
  const ctx = useContext(PrincipalContext);
  if (!ctx) {
    return {
      principalId: 'anonymous',
      nodeId: 'anonymous',
      displayName: 'Anonymous',
      avatarIcon: '👤',
      isOwner: false,
      isSelf: (authorPrincipalId?: string, authorId?: string) => {
        if (authorPrincipalId === 'self' || authorId === 'self') return true;
        return false;
      },
    };
  }
  return ctx;
}
